import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
const toast = { success: (m) => console.log("[toast]", m), error: (m) => console.error("[toast]", m), info: (m) => console.log("[toast]", m) };
import PageContainer, { PageHeader } from "@/components/PageContainer";
import api from "@/lib/assessments";
import { readAll } from "@/lib/assessments";
const formatApiError = (e) => (e && e.message) || "Unknown error";
import { Save, ChevronLeft, Check, X } from "lucide-react";
import { FRAMEWORK_MAP_ITEMS, interpretFrameworkMap } from "@/data/instruments";

export default function FrameworkMap() {
  const navigate = useNavigate();
  const [patient, setPatient] = useState("");
  const [notes, setNotes] = useState("");
  const [inPlay, setInPlay] = useState({});
  const [evidence, setEvidence] = useState({});
  const [saving, setSaving] = useState(false);

  const active = Object.values(inPlay).filter(Boolean).length;
  const interp = interpretFrameworkMap(inPlay);
  const missing = FRAMEWORK_MAP_ITEMS.filter((f) => !inPlay[f.id]);

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/assessments", {
        kind: "framework_map",
        patient_identifier: patient || null,
        responses: { in_play: inPlay, evidence, missing: missing.map((m) => m.id) },
        score: active,
        interpretation: interp.band,
        notes: notes || null,
      });
      toast.success("Framework Interaction Map saved.");
      navigate("/portal/continuum/tools/history");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <PageContainer testid="framework-map-page">
      <Link to="/portal/continuum/tools" className="text-sm text-ink-muted hover:text-navy inline-flex items-center gap-1 mb-4" data-testid="fmap-back">
        <ChevronLeft size={14} strokeWidth={2} /> All tools
      </Link>

      <PageHeader overline="Portal · Tool" title="Framework Interaction Map"
        subtitle="Mark which of the nine core frameworks are actively in play for this patient and add one line of evidence. Highlights framework gaps." />

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          <div className="card p-5">
            <label className="label">Patient identifier</label>
            <input className="field" value={patient} onChange={(e) => setPatient(e.target.value)} placeholder="Initials or MRN" data-testid="fmap-patient" />
          </div>

          <div className="card p-5 space-y-4">
            {FRAMEWORK_MAP_ITEMS.map((f) => (
              <div key={f.id} className="pb-4 border-b border-line-soft last:pb-0 last:border-0" data-testid={`fmap-item-${f.id}`}>
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div>
                    <div className="text-navy font-medium">{f.label}</div>
                    <div className="text-xs text-ink-muted mt-0.5">{f.usage}</div>
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    <button
                      onClick={() => setInPlay({ ...inPlay, [f.id]: true })}
                      className={`btn text-sm ${inPlay[f.id] === true ? "btn-primary" : "btn-secondary"}`}
                      data-testid={`fmap-${f.id}-yes`}
                    >
                      <Check size={13} strokeWidth={2.5} /> In play
                    </button>
                    <button
                      onClick={() => setInPlay({ ...inPlay, [f.id]: false })}
                      className={`btn text-sm ${inPlay[f.id] === false ? "btn-primary" : "btn-secondary"}`}
                      data-testid={`fmap-${f.id}-no`}
                    >
                      <X size={13} strokeWidth={2.5} /> Not yet
                    </button>
                  </div>
                </div>
                {inPlay[f.id] === true && (
                  <input
                    className="field text-sm mt-2 fade-up"
                    placeholder="One line of evidence (e.g., 'PHQ-9 completed at every visit')"
                    value={evidence[f.id] || ""}
                    onChange={(e) => setEvidence({ ...evidence, [f.id]: e.target.value })}
                    data-testid={`fmap-${f.id}-evidence`}
                  />
                )}
              </div>
            ))}
          </div>

          <div className="card p-5">
            <label className="label">Notes</label>
            <textarea className="field min-h-[80px]" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="fmap-notes" />
          </div>
        </div>

        <aside className="lg:sticky lg:top-6 self-start">
          <div className="card p-6" data-testid="fmap-summary">
            <div className="overline mb-2">Coverage</div>
            <div className="text-5xl font-semibold text-navy">{active}<span className="text-xl text-ink-muted"> / 9</span></div>
            <div className="mt-4">
              <div className="badge badge-teal mb-2">{interp.band}</div>
              <p className="text-sm text-ink leading-relaxed">{interp.detail}</p>
            </div>

            {missing.length > 0 && (
              <>
                <div className="rule my-4" />
                <div className="text-xs uppercase tracking-wider text-rust mb-2">Gaps</div>
                <ul className="text-sm space-y-1">
                  {missing.map((m) => (
                    <li key={m.id} className="text-ink">· {m.label}</li>
                  ))}
                </ul>
              </>
            )}

            <div className="rule my-4" />
            <button onClick={save} disabled={saving} className="btn btn-primary w-full justify-center" data-testid="fmap-save">
              {saving ? "Saving..." : (<><Save size={14} strokeWidth={2} /> Save to history</>)}
            </button>
          </div>
        </aside>
      </div>
    </PageContainer>
  );
}
